import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, X } from 'lucide-react';
import {
  Application,
  REJECTION_REASON_LABELS,
  type OrgUser,
  type RejectionReasonCode,
  type JobStatus,
} from '@core/types';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { JOB_OPS_STATUS_LABELS } from '@core/constants/jobLifecycle';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { CandidateVerticalTimeline } from './CandidateVerticalTimeline';
import { buildSingleApplicationTimeline } from './candidateDetailTimelines';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

function fmt(iso: string) {
  const s = formatDateTimeGmt(iso);
  return s === '—' ? iso : s;
}

function ago(iso: string): string {
  try {
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d < 1) return 'today';
    if (d === 1) return 'yesterday';
    if (d < 14) return `${d}d ago`;
    if (d < 60) return `${Math.floor(d / 7)}w ago`;
    return `${Math.floor(d / 30)}mo ago`;
  } catch { return '—'; }
}

function shortId(id: string) {
  return id.length <= 12 ? id : `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function resolveMember(team: OrgUser[], key: string | null | undefined): string {
  if (!key) return '—';
  const m = team.find((u) => u.keycloak_sub === key || u.id === key);
  return m?.display_name?.trim() || m?.email || shortId(key);
}

function fitmentKeys(json: Record<string, unknown> | null | undefined): string {
  if (!json) return '—';
  const keys = Object.keys(json);
  return keys.length === 0 ? '—' : keys.slice(0, 10).join(', ') + (keys.length > 10 ? '…' : '');
}

/** One label+value row inside a section */
function Row({ label, value, danger, wide }: { label: string; value: React.ReactNode; danger?: boolean; wide?: boolean }) {
  return (
    <>
      <dt className={`vscode-text-secondary pr-2 min-w-0 text-sm ${wide ? 'sm:col-span-1' : ''}`}>
        {label}
      </dt>
      <dd className={`min-w-0 break-words text-sm ${danger ? 'text-red-400/90' : 'vscode-text-primary'}`}>
        {value}
      </dd>
    </>
  );
}

type Props = {
  app: Application;
  teamMembers: OrgUser[];
  onClose: () => void;
};

export function CandidateApplicationDetailModal({ app, teamMembers, onClose }: Props) {
  /* events sorted newest-first; ascending=true will reverse them in the component */
  const timeline = buildSingleApplicationTimeline(app);
  const jobLabel = app.job?.status
    ? JOB_OPS_STATUS_LABELS[app.job.status as JobStatus] ?? String(app.job.status)
    : null;

  /* Close on Escape */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[110] overflow-y-auto bg-black/60 backdrop-blur-md"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`Application: ${app.job?.title ?? '—'}`}
    >
      {/* pt-14 pushes the card below the h-14 topbar; pb-6 gives breathing room */}
      <div className="flex min-h-full items-start justify-center px-3 pt-16 pb-6">
      <div className="relative w-full max-w-4xl bg-[var(--vscode-bg-elevated)] border border-[var(--vscode-border-primary)] rounded-xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 80px)' }}>

        {/* ── Header — solid GGJ purple (#8B80F9) matching B2C brand ── */}
        <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ background: 'var(--brand-popup-header, #8B80F9)' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-white/20 border border-white/30">
            <Briefcase className="w-5 h-5 text-white" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-white truncate leading-tight">
              {app.job?.title ?? '—'}
            </p>
            <p className="text-xs text-white/75 truncate font-medium">{app.client?.name ?? '—'}</p>
          </div>
          <StatusBadge status={app.status} />
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-1 w-7 h-7 flex items-center justify-center rounded-md shrink-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Body: two columns ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(240px,30%)] divide-y lg:divide-y-0 lg:divide-x divide-[var(--vscode-border-primary)] flex-1 min-h-0 overflow-hidden">

          {/* LEFT: attribute sections — scrollable */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>

            {/* Job & Client */}
            <div className="ggj-profile-section-h">Job &amp; Client</div>
            <div className="ggj-profile-section-body">
              <dl className="grid grid-cols-1 sm:grid-cols-[minmax(150px,190px)_1fr] gap-x-4 gap-y-0">
                <Row label="Job title" value={app.job?.title ?? '—'} />
                <Row label="Client" value={app.client?.name ?? '—'} />
                {app.client?.contact_name && <Row label="Client contact" value={app.client.contact_name} />}
                {app.client?.contact_email && (
                  <Row label="Contact email" value={
                    <a href={`mailto:${app.client.contact_email}`} className="text-brand-400 hover:underline">
                      {app.client.contact_email}
                    </a>
                  } />
                )}
                {jobLabel && <Row label="Job ops status" value={jobLabel} />}
                <Row label="Submitted" value={`${fmt(app.submitted_at)} · ${ago(app.submitted_at)}`} />
                {app.job_id && (
                  <Row label="Job posting" value={
                    <Link to={`/jobs/${app.job_id}`} className="text-brand-400 hover:underline text-xs" onClick={onClose}>
                      [↗ View job]
                    </Link>
                  } />
                )}
              </dl>
            </div>

            {/* Pipeline status */}
            <div className="ggj-profile-section-h">Pipeline status</div>
            <div className="ggj-profile-section-body">
              <dl className="grid grid-cols-1 sm:grid-cols-[minmax(150px,190px)_1fr] gap-x-4 gap-y-0">
                <Row label="Current stage" value={<StatusBadge status={app.status} />} />
                <Row label="Stage label" value={PIPELINE_STATUS_LABEL[app.status] ?? app.status} />
                <Row label="Stage changed" value={fmt(app.status_updated_at)} />
                <Row label="Owner" value={app.assigned_user?.display_name ?? resolveMember(teamMembers, app.assigned_to)} />
                <Row label="Moved by" value={resolveMember(teamMembers, app.status_updated_by)} />
                {app.pipeline_lock_holder && (
                  <Row label="Pipeline lock holder" value={app.pipeline_lock_holder.display_name ?? '—'} />
                )}
                {app.pipeline_lock_expires_at && (
                  <Row label="Lock expires" value={fmt(app.pipeline_lock_expires_at)} />
                )}
              </dl>
            </div>

            {/* Fitment & scoring */}
            <div className="ggj-profile-section-h">Fitment &amp; scoring</div>
            <div className="ggj-profile-section-body">
              <dl className="grid grid-cols-1 sm:grid-cols-[minmax(150px,190px)_1fr] gap-x-4 gap-y-0">
                <Row label="Role-fit score" value={app.fitment_score != null ? `${app.fitment_score} / 100` : '—'} />
                <Row label="Fitment dimensions" value={fitmentKeys(app.fitment_json)} />
                <Row
                  label="Résumé ATS"
                  value={app.optimization
                    ? `${app.optimization.ats_score_before ?? '—'} → ${app.optimization.ats_score_after ?? '—'}`
                    : '—'
                  }
                />
              </dl>
            </div>

            {/* Commercial */}
            <div className="ggj-profile-section-h">Commercial</div>
            <div className="ggj-profile-section-body">
              <dl className="grid grid-cols-1 sm:grid-cols-[minmax(150px,190px)_1fr] gap-x-4 gap-y-0">
                <Row label="Placement date" value={app.placement_date ? fmt(app.placement_date) : '—'} />
                <Row label="Placement fee" value={app.placement_fee != null ? String(app.placement_fee) : '—'} />
                <Row label="Offer notes" value={app.offer_notes ?? '—'} />
              </dl>
            </div>

            {/* Notes & audit */}
            <div className="ggj-profile-section-h">Notes &amp; audit</div>
            <div className="ggj-profile-section-body">
              <dl className="grid grid-cols-1 sm:grid-cols-[minmax(150px,190px)_1fr] gap-x-4 gap-y-0">
                {app.rejection_reason_code && (
                  <Row
                    label="Rejection"
                    value={`${REJECTION_REASON_LABELS[app.rejection_reason_code as RejectionReasonCode] ?? app.rejection_reason_code}${app.rejection_reason_text ? ` — ${app.rejection_reason_text}` : ''}`}
                    danger
                  />
                )}
                <Row
                  label="Internal notes"
                  value={app.internal_notes
                    ? <span className="whitespace-pre-wrap">{app.internal_notes}</span>
                    : '—'
                  }
                />
                <Row label="Created" value={fmt(app.created_at)} />
                <Row label="Last updated" value={fmt(app.updated_at)} />
              </dl>
            </div>

            {/* Open full record */}
            <div className="px-4 py-3 border-t border-[var(--vscode-border-primary)]">
              <Link
                to={`/applications/${app.id}`}
                className="ggj-toolbar-btn"
                onClick={onClose}
              >
                [↗ Open full application record]
              </Link>
            </div>
          </div>

          {/* RIGHT: heartbeat timeline — ascending, current status at bottom with pulse */}
          <div className="overflow-y-auto p-4 space-y-3 bg-[color-mix(in_srgb,var(--vscode-primary)_3%,var(--vscode-bg-secondary))]" style={{ maxHeight: 'calc(100vh - 140px)' }}>
            <p className="text-[11px] font-bold uppercase tracking-wider vscode-text-tertiary">Application timeline</p>
            <CandidateVerticalTimeline
              events={timeline}
              emptyMessage="No events recorded."
              ascending
            />
          </div>
        </div>
      </div>
      </div>{/* end centering wrapper */}
    </div>
  );
}
